import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, IconButton } from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';

const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes of inactivity

export function useScreensaver() {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);
  const timerRef = useRef(null);

  const clear = useCallback(() => clearTimeout(timerRef.current), []);

  const show = useCallback(() => {
    visibleRef.current = true;
    setVisible(true);
    clearTimeout(timerRef.current);
  }, [clear]);

  const hide = useCallback(() => {
    visibleRef.current = false;
    setVisible(false);
  }, []);

  const resetTimer = useCallback(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(show, IDLE_TIMEOUT);
  }, [show, clear]);

  useEffect(() => {
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'scroll'];
    const onActivity = () => {
      if (visibleRef.current) return;
      resetTimer();
    };
    events.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    resetTimer();
    return () => {
      events.forEach((e) => window.removeEventListener(e, onActivity));
      clearTimeout(timerRef.current);
    };
  }, [resetTimer, clear]);

  return { visible, show, hide };
}

export default function Screensaver({ visible, onHide }) {
  return (
    <Box
      onClick={onHide}
      className="screensaver"
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999,
        backgroundImage: 'url(/owntrip_banner_4k.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        cursor: 'pointer',
        transform: visible ? 'translateY(0)' : 'translateY(-100%)',
        transition: visible ? 'transform .35s ease' : 'transform .05s ease',
      }}
    >
      <Box
        className="screensaver-hide-hint"
        sx={{
          position: 'absolute',
          left: '50%',
          bottom: 24,
          transform: 'translateX(-50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'rgba(0,0,0,0.35)',
          border: '2px solid rgba(255,255,255,0.7)',
          color: '#fff',
        }}
      >
        <KeyboardArrowUpRoundedIcon sx={{ fontSize: 40 }} />
      </Box>
    </Box>
  );
}

export function ScreensaverToggle({ onShow }) {
  return (
    <IconButton
      onClick={onShow}
      title="Open screensaver"
      size="small"
      sx={{
        position: 'absolute',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        border: '1px solid #e2e8f0',
        borderRadius: 2,
        color: '#475569',
        bgcolor: '#fff',
        zIndex: 5,
        '&:hover': { bgcolor: '#f1f5f9' },
      }}
    >
      <KeyboardArrowDownRoundedIcon fontSize="small" />
    </IconButton>
  );
}